import type { BadgeUniverso, PercursoStep, PercursoStop } from 'shared';

const TIME_ZONE = 'Europe/Lisbon';

/** How a stop is drawn on the trail, from the most to the least notable. */
export type StopMarker = 'badge' | 'session' | 'equipment' | 'visit';

/** A row of the trail: a day, or the month that the days below it belong to. */
export type TrailRow =
  | { kind: 'month'; key: string; label: string }
  | { kind: 'stop'; key: string; stop: PercursoStop };

export interface TrailPoint {
  x: number;
  y: number;
}

/**
 * Where each marker sits across the trail's gutter, in px. Cycled so the line
 * winds like a path rather than zig-zagging on a beat.
 */
const MARKER_LANES = [24, 48, 34, 52, 26, 44] as const;

export function markerLane(index: number): number {
  return MARKER_LANES[index % MARKER_LANES.length];
}

export function stopMarker(stop: PercursoStop): StopMarker {
  const kinds = new Set(stop.steps.map((s) => s.kind));
  if (kinds.has('badge')) return 'badge';
  if (kinds.has('session')) return 'session';
  if (kinds.has('equipment')) return 'equipment';
  return 'visit';
}

/** The universo that colours a stop: its first badge's, else its first session's. */
export function stopUniverso(stop: PercursoStop): BadgeUniverso | null {
  const badge = stop.steps.find((s) => s.kind === 'badge' && s.universo);
  if (badge?.universo) return badge.universo;
  const session = stop.steps.find((s) => s.kind === 'session' && s.universo);
  return session?.universo ?? null;
}

/** The centres a stop happened in, each once, in the order first seen. */
export function stopPlaces(stop: PercursoStop): string[] {
  return stop.steps
    .map((s) => s.place)
    .filter((place, i, all) => place !== '' && all.indexOf(place) === i);
}

/**
 * The rows of the trail, newest first, with a month heading wherever the
 * month changes. `stops` arrive oldest first, as the API sends them.
 */
export function trailRows(stops: ReadonlyArray<PercursoStop>): TrailRow[] {
  return [...stops].reverse().flatMap((stop, i, newestFirst): TrailRow[] => {
    const month = stop.day.slice(0, 7);
    const row: TrailRow = { kind: 'stop', key: stop.day, stop };
    if (i > 0 && newestFirst[i - 1].day.slice(0, 7) === month) return [row];
    return [{ kind: 'month', key: `m-${month}`, label: formatMonth(stop.day) }, row];
  });
}

/**
 * An SVG path through the points, top to bottom: each leg is an S-curve that
 * leaves and arrives vertically, so the trail bends between lanes.
 */
export function trailPath(points: ReadonlyArray<TrailPoint>): string {
  if (points.length === 0) return '';
  const [first, ...rest] = points;
  const legs = rest.map((p, i) => {
    const prev = points[i];
    const mid = (p.y - prev.y) / 2;
    return `C ${prev.x} ${prev.y + mid} ${p.x} ${p.y - mid} ${p.x} ${p.y}`;
  });
  return [`M ${first.x} ${first.y}`, ...legs].join(' ');
}

// ─── Formatting ──────────────────────────────────────────────────────────────

/** A "YYYY-MM-DD" day as a Date at noon UTC, so no time zone moves it. */
function dayAsDate(day: string): Date {
  return new Date(`${day}T12:00:00Z`);
}

export function formatDay(day: string): string {
  return dayAsDate(day).toLocaleDateString('pt-PT', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

export function formatMonth(day: string): string {
  return dayAsDate(day).toLocaleDateString('pt-PT', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-PT', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  });
}

export function formatSpan(step: PercursoStep): string {
  return step.endedAt
    ? `${formatTime(step.at)}–${formatTime(step.endedAt)}`
    : `desde ${formatTime(step.at)}`;
}

/** A length of time as "45 min" or "1 h 20". */
export function formatDuration(fromIso: string, toIso: string): string {
  const minutes = Math.max(
    0,
    Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60_000),
  );
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return rest === 0 ? `${minutes / 60} h` : `${Math.floor(minutes / 60)} h ${String(rest).padStart(2, '0')}`;
}

export function formatHours(hours: number): string {
  return hours.toLocaleString('pt-PT', { maximumFractionDigits: 1 });
}
